import { FC, useId, useState } from 'react'
import { useIntl } from 'react-intl'
import { Globe, ShieldAlert } from 'lucide-react'
import type { ApprovalRequestCall } from '../../../app/modules/types/chatBot'
import type { ApprovalDecision } from '../../../services/api/chat.api'
import {
  DENIAL_MESSAGE_MAX,
  buildDecisions,
  isFullyDecided,
  type ApprovalCardState,
} from './approvalState'
import { neutraliseBidi } from './textSafety'

interface Props {
  card: ApprovalCardState
  /**
   * Whether the current user owns this chat — `me.id === chat.user_id` (§9).
   *
   * Approvals are the ONE thing on a chat that is not org-scoped (§12:850-861):
   * a same-org colleague can read every message here but gets a 403 on the
   * decision, because the resumed run acts under the owner's identity, against
   * the owner's SSH session and the owner's scan quota. So this does not merely
   * grey out the buttons — it removes them, and says whose call it is instead.
   */
  isOwner: boolean
  /**
   * Whether `isOwner` is an ANSWER yet.
   *
   * Ownership is read from `GET /chat/{id}`, which can be pending or can fail —
   * and a boolean cannot say so. Collapsing "not loaded" into `isOwner: false`
   * makes this card assert "only the chat owner can decide this" at the owner,
   * and on a failed read it says it permanently, with no retry and no way to
   * tell the lie from the truth. So the unknown case gets its own, honest
   * treatment: no buttons (we cannot promise they would work) and no denial
   * either.
   */
  ownershipKnown: boolean
  /**
   * Receives a WIRE-READY batch, already through `buildDecisions`. The parent
   * posts it verbatim to `POST /chat/{id}/approvals`; it does no assembly and
   * no trimming of its own.
   */
  onSubmit: (decisions: ApprovalDecision[]) => void
  isPending: boolean
}

/**
 * Render one argument value.
 *
 * Strings pass through un-re-serialised — a `command` must be legible
 * character for character, and quoting it would add delimiters the user might
 * read as part of the command. Everything else is stringified, and only a
 * genuinely structured value (object/array) becomes JSON — a per-key JSON
 * fragment, not the collapsed blob §12:747 rules out.
 *
 * `JSON.stringify` escapes control characters below U+0020 but passes bidi
 * controls straight through, so the object path needs `neutraliseBidi` just as
 * much as the string path does.
 */
function formatArgValue(value: unknown): string {
  if (typeof value === 'string') return neutraliseBidi(value)
  if (value === null || value === undefined) return String(value)
  if (typeof value === 'object') return neutraliseBidi(JSON.stringify(value, null, 2))
  return String(value)
}

/**
 * The approval card: one halted turn's whole batch of gated tool calls.
 *
 * Two things here are security surface rather than styling.
 *
 * ARGUMENTS ARE RENDERED IN FULL, per key, always (§12:745-747). This card is
 * where a user confirms which host, username and port `start_patch_session`
 * may reach — before any keypair exists, and precisely the parameters a prompt
 * injection would try to steer. A collapsed disclosure or a `JSON.stringify`
 * blob invites approval-by-reflex, so neither is used, and the arguments stay
 * on screen after a decision so the record of what was authorised survives.
 *
 * THE OWNER GATE REMOVES CONTROLS RATHER THAN DISABLING THEM. See `isOwner`.
 *
 * Three near-identical states are deliberately driven from three different
 * sources (§12:806-809), and conflating any two is a real bug:
 *
 *  - a per-call button        -> that call's own `decision !== null`
 *  - the batch approve-all    -> how many calls are still undecided
 *  - the card's "done" look   -> `card.state === 'resolved'`
 *
 * `state` flips only once the approved work has EXECUTED, so a fully-decided
 * card legitimately sits `open` while the tools run; that gap is the
 * `RUNNING` message below, not a "done" card.
 */
const ApprovalCard: FC<Props> = ({ card, isOwner, ownershipKnown, onSubmit, isPending }) => {
  const intl = useIntl()
  const uid = useId()
  /** The one call whose deny form is open, by `tool_call_id`; null for none. */
  const [denyingId, setDenyingId] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  /**
   * `fromStreamApprovals([])` already returns `null`, but the persisted path
   * does not: a block with `calls: []` yields a perfectly valid card that would
   * paint an empty bordered box in the middle of a conversation. The service is
   * not supposed to emit one, which is exactly why nothing upstream would catch
   * it if it ever did. One line, and the failure mode becomes "nothing" instead
   * of "an empty thing the user cannot act on".
   */
  if (card.calls.length === 0) return null

  const isResolved = card.state === 'resolved'
  const undecided = card.calls.filter((call) => call.decision === null)
  const decidedCount = card.calls.length - undecided.length
  /** Controls are live only for a KNOWN owner, and only while work remains. */
  const canDecide = ownershipKnown && isOwner && !isResolved && undecided.length > 0
  /** Something is still undecided, so the gate's verdict is worth stating. */
  const gateApplies = !isResolved && undecided.length > 0

  /**
   * Clears the deny form only when THIS submission is the one that settles the
   * call the form belongs to. Approving call B used to discard a reason typed
   * against call A: nothing cross-contaminated, but the user lost written work
   * to an unrelated click. Approve-all still clears it, correctly — it decides
   * every undecided call, including that one.
   */
  const submit = (decisions: ApprovalDecision[]) => {
    if (denyingId !== null && decisions.some((decision) => decision.tool_call_id === denyingId)) {
      setDenyingId(null)
      setReason('')
    }
    onSubmit(decisions)
  }

  const approveOne = (call: ApprovalRequestCall) =>
    submit(buildDecisions([{ tool_call_id: call.tool_call_id, approved: true }]))

  /**
   * One request for every call still awaiting a decision — the reason the
   * endpoint takes a batch at all. Permissions are still checked per call
   * server-side; batching is transport, not a relaxation of the gate.
   */
  const approveAll = () =>
    submit(buildDecisions(undecided.map((call) => ({ tool_call_id: call.tool_call_id, approved: true }))))

  /**
   * `reason` goes across raw. `buildDecisions` owns trimming, dropping a blank
   * reason rather than sending `''` the model would read as a stated reason,
   * and truncating at {@link DENIAL_MESSAGE_MAX}. Duplicating any of that here
   * would be a second implementation free to drift from the one the request is
   * actually built by.
   */
  const confirmDeny = (call: ApprovalRequestCall) =>
    submit(buildDecisions([{ tool_call_id: call.tool_call_id, approved: false, reason }]))

  const cancelDeny = () => {
    setDenyingId(null)
    setReason('')
  }

  const renderArgs = (call: ApprovalRequestCall) => {
    /*
     * `args` is typed `Record<string, unknown>`, but this is a wire boundary
     * and the type describes a promise rather than what arrived. The
     * PERSISTED path — the authoritative one, per §12 step 3 — is the
     * undefended one: `isApprovalRequestBlock` only checks that `calls` is an
     * array, and `fromMessageBlocks` hands the block through by reference,
     * whereas the stream path already coalesces with `?? {}`. §12 also records
     * that `args` was a JSON *string* on the old wire, so a non-object here is
     * documented drift rather than paranoia.
     *
     * `Object.entries(null)` throws, and this renders inside the message list,
     * so that throw takes down the whole transcript rather than one card.
     * Degrade to "no arguments" instead.
     */
    const entries =
      call.args && typeof call.args === 'object' ? Object.entries(call.args) : []
    if (entries.length === 0) {
      return (
        <p className='lum-approval-noargs text-muted small mb-0'>
          {intl.formatMessage({ id: 'CHAT.APPROVAL.NO_ARGS' })}
        </p>
      )
    }
    return (
      <dl className='lum-approval-args mb-2'>
        {entries.map(([key, value]) => (
          <div className='lum-approval-arg' key={key}>
            {/* Keys are model-generated too, so they get the same treatment. */}
            <dt className='lum-approval-arg-key'>{neutraliseBidi(key)}</dt>
            {/*
              `command` keeps the terminal treatment the previous card gave it.
              It is the single highest-risk argument on the highest-risk gated
              tool, and a monospace block is what makes a trailing `&& rm -rf`
              visible rather than something that blends into prose. It is a
              presentation difference only: every other key is rendered just as
              completely, one row each, so nothing is hidden by not matching.
            */}
            <dd
              className={`lum-approval-arg-value mb-0${
                key === 'command' ? ' lum-terminal rounded px-2 py-1' : ''
              }`}
            >
              {formatArgValue(value)}
            </dd>
          </div>
        ))}
      </dl>
    )
  }

  /**
   * The SSH arm: an interactive session on `host:port` as `username`. Same
   * rendering `renderTarget` always gave a target, before the union — kept a
   * separate function only because the two arms now validate and label
   * differently.
   */
  const renderSshTarget = (target: Extract<ApprovalRequestCall['target'], { kind: 'ssh' }>) => {
    if (
      typeof target.host !== 'string' ||
      typeof target.username !== 'string' ||
      // `host` and `username` distrust the wire; `port` gets the same
      // scrutiny rather than being interpolated raw. Skipping it would
      // render `deploy@web-01:undefined` on the one line whose whole job is
      // telling the approver which machine is real.
      typeof target.port !== 'number'
    ) {
      return null
    }

    // A host comes from scanned asset data, so it gets the same bidi
    // treatment as model-generated text — see `neutraliseBidi`'s own comment.
    const where = neutraliseBidi(`${target.username}@${target.host}:${target.port}`)

    return (
      <p className='lum-approval-target mb-2' data-testid='approval-target'>
        <span className='lum-approval-target-label text-muted small'>
          {intl.formatMessage({ id: 'CHAT.APPROVAL.TARGET_LABEL' })}
        </span>{' '}
        <code dir='ltr'>{where}</code>
      </p>
    )
  }

  /**
   * The verify arm: a one-shot outbound reachability check the sandbox runs
   * itself — no session, no keypair, nothing interactive. Styled and labelled
   * distinctly from the SSH line on purpose: `lum-approval-target--verify`
   * carries a different accent (see `chat.scss`) and its own copy, so an
   * approver cannot mistake "the agent will poll these ports once" for "the
   * agent gets a shell on this host".
   */
  const renderVerifyTarget = (target: Extract<ApprovalRequestCall['target'], { kind: 'verify' }>) => {
    if (
      typeof target.host !== 'string' ||
      typeof target.ip !== 'string' ||
      !Array.isArray(target.ports) ||
      target.ports.some((port) => typeof port !== 'number')
    ) {
      return null
    }

    // Host and ip are resolved from asset data, so the same bidi defence
    // applies as the SSH arm. The port list is numeric-only (just checked
    // above) and the "ports" copy is our own, so neither needs it.
    const where = neutraliseBidi(`${target.host} (${target.ip})`)
    const ports =
      target.ports.length > 0
        ? intl.formatMessage({ id: 'CHAT.APPROVAL.TARGET_VERIFY_PORTS' }, { ports: target.ports.join(', ') })
        : null

    return (
      <p className='lum-approval-target lum-approval-target--verify mb-2' data-testid='approval-target'>
        <Globe size={13} aria-hidden='true' />
        <span className='lum-approval-target-label text-muted small'>
          {intl.formatMessage({ id: 'CHAT.APPROVAL.TARGET_VERIFY_LABEL' })}
        </span>{' '}
        <code dir='ltr'>
          {where}
          {ports ? ` · ${ports}` : ''}
        </code>
      </p>
    )
  }

  /**
   * The machine (or endpoint) this call will act on, above the arguments and
   * visually distinct from them.
   *
   * Deliberately not just another `renderArgs` row. Everything in `args` is
   * what the MODEL asked for; this is what the service resolved that request
   * to. Presenting them identically would invite reading a model-claimed host
   * as a confirmed one, which is the misreading this line exists to prevent.
   *
   * `target` is a discriminated union now (SSH session vs. outbound verify
   * check), and the shape is checked rather than trusted either way: it
   * crosses the wire, and this renders inside the message list, where a throw
   * costs the whole transcript instead of one card. An unrecognised or
   * missing `kind` renders nothing — the same "no target line" outcome a
   * missing target has always had.
   */
  const renderTarget = (call: ApprovalRequestCall) => {
    const target = call.target
    if (!target || typeof target !== 'object') return null
    if (target.kind === 'ssh') return renderSshTarget(target)
    if (target.kind === 'verify') return renderVerifyTarget(target)
    return null
  }

  const renderOutcome = (call: ApprovalRequestCall) => (
    <div className='lum-approval-outcome'>
      <span className={`lum-status-badge lum-status-${call.decision === 'approved' ? 'approved' : 'rejected'}`}>
        {intl.formatMessage({
          id: call.decision === 'approved' ? 'CHAT.APPROVAL.DECISION_APPROVED' : 'CHAT.APPROVAL.DECISION_DENIED',
        })}
      </span>
      {/*
        The reason the user gave, replayed from `denial_message` rather than
        from local state, so a reload still explains why something was refused
        — and so does the model, which was handed the same string.
      */}
      {call.decision === 'denied' && call.denial_message ? (
        <p className='lum-approval-denial small mb-0 mt-1'>
          <span className='text-muted me-1'>
            {intl.formatMessage({ id: 'CHAT.APPROVAL.DENIAL_REASON' })}
          </span>
          <span className='lum-approval-denial-text'>{call.denial_message}</span>
        </p>
      ) : null}
    </div>
  )

  const renderActions = (call: ApprovalRequestCall) => {
    if (denyingId !== call.tool_call_id) {
      return (
        <div className='d-flex gap-2'>
          <button
            type='button'
            className='btn btn-sm btn-success'
            onClick={() => approveOne(call)}
            disabled={isPending}
          >
            {intl.formatMessage({ id: 'CHAT.APPROVAL.APPROVE' })}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-outline-danger'
            onClick={() => {
              setDenyingId(call.tool_call_id)
              setReason('')
            }}
            disabled={isPending}
          >
            {intl.formatMessage({ id: 'CHAT.APPROVAL.DENY' })}
          </button>
        </div>
      )
    }

    const reasonId = `${uid}-deny-${call.tool_call_id}`
    return (
      <div className='d-flex flex-column gap-2'>
        <label className='form-label small text-muted mb-0' htmlFor={reasonId}>
          {intl.formatMessage({ id: 'CHAT.APPROVAL.DENY_REASON_LABEL' })}
        </label>
        <textarea
          id={reasonId}
          className='form-control form-control-sm'
          rows={2}
          // Free prose headed for the model, so it reads in the direction it is
          // written in — same rule as the composer, including why it is
          // conditional: `dir='auto'` on an empty field resolves to `ltr` and
          // would drag the Persian placeholder with it.
          dir={reason.trim() ? 'auto' : undefined}
          /*
           * Clicking Deny unmounts the button that had focus, dropping focus to
           * `<body>` — a keyboard user would have to tab from the top of the
           * page to reach the field they just asked for. Moving focus here is
           * what the click already meant.
           */
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          // Same constant the request builder caps at, so the user is stopped
          // at the boundary instead of being silently truncated afterwards.
          maxLength={DENIAL_MESSAGE_MAX}
          placeholder={intl.formatMessage({ id: 'CHAT.APPROVAL.DENY_REASON_PLACEHOLDER' })}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <div className='d-flex gap-2'>
          <button
            type='button'
            className='btn btn-sm btn-danger'
            onClick={() => confirmDeny(call)}
            disabled={isPending}
          >
            {intl.formatMessage({ id: 'CHAT.APPROVAL.CONFIRM_DENY' })}
          </button>
          <button type='button' className='btn btn-sm btn-link' onClick={cancelDeny} disabled={isPending}>
            {intl.formatMessage({ id: 'CHAT.APPROVAL.CANCEL' })}
          </button>
        </div>
      </div>
    )
  }

  const renderStatus = () => {
    if (isResolved) {
      return intl.formatMessage({ id: 'CHAT.APPROVAL.RESOLVED' })
    }
    if (isFullyDecided(card)) {
      // Decided but not yet executed — the legitimate `open` gap above.
      return intl.formatMessage({ id: 'CHAT.APPROVAL.RUNNING' })
    }
    if (decidedCount > 0) {
      // NOT an error (§12:840-845). A partly-decided batch cannot resume at
      // all, nothing has executed, and the rest still need a decision.
      return intl.formatMessage({ id: 'CHAT.APPROVAL.WAITING_MORE' }, { count: undecided.length })
    }
    return null
  }

  const status = renderStatus()

  return (
    <div
      className={`lum-approval-card rounded p-3 my-2 ${
        isResolved ? 'lum-approval-resolved' : 'border border-warning'
      }`}
      role='group'
      // Points at the heading rather than repeating it in an `aria-label`,
      // which would have a screen reader announce the same sentence twice.
      aria-labelledby={`${uid}-title`}
    >
      <div className='lum-approval-header d-flex align-items-center gap-2 mb-2'>
        <ShieldAlert size={16} aria-hidden='true' />
        <span className='fw-semibold' id={`${uid}-title`}>
          {intl.formatMessage({ id: 'CHAT.APPROVAL.TITLE' })}
        </span>
        <span className='lum-approval-spacer flex-grow-1' />
        {/*
          Offered only while more than one call is undecided: with one left it
          is the same click as that call's own Approve, and a second button
          doing the same thing is one more chance to click without reading.
        */}
        {canDecide && undecided.length > 1 ? (
          <button
            type='button'
            className='btn btn-sm btn-outline-success'
            onClick={approveAll}
            disabled={isPending}
          >
            {intl.formatMessage({ id: 'CHAT.APPROVAL.APPROVE_ALL' }, { count: undecided.length })}
          </button>
        ) : null}
      </div>

      {canDecide ? (
        <p className='lum-approval-hint small text-muted mb-2'>
          {intl.formatMessage({ id: 'CHAT.APPROVAL.CHECK_ARGS' })}
        </p>
      ) : null}

      <ul className='lum-approval-calls list-unstyled mb-0'>
        {card.calls.map((call) => (
          <li className='lum-approval-call' key={call.tool_call_id}>
            {/*
              Grouped and labelled by its own tool heading. Without this a
              screen reader in forms mode, or a rotor button list, sees N
              identical "Approve" buttons with nothing tying each to its call —
              on a card that can batch `run_command` with `start_patch_session`,
              that is a wrong-call-approved risk. The group sits inside the
              `<li>` rather than replacing its role so the batch still reads as
              a list of N.
            */}
            <div role='group' aria-labelledby={`${uid}-tool-${call.tool_call_id}`}>
              <p className='lum-approval-tool mb-1' id={`${uid}-tool-${call.tool_call_id}`}>
                {intl.formatMessage({ id: 'CHAT.APPROVAL.WANTS_TO_RUN' })}{' '}
                <code className='lum-approval-tool-name'>{call.tool_name}</code>
              </p>
              {renderTarget(call)}
              {renderArgs(call)}
            {/*
              Per §12:809 this keys off THIS call's decision, never off the
              card-level "every call decided" predicate: on a multi-call card
              that predicate stays false until the last decision lands, which
              would leave an already-decided call clickable and 409-ing.
            */}
              {call.decision !== null
                ? renderOutcome(call)
                : ownershipKnown && isOwner && !isResolved
                  ? renderActions(call)
                  : null}
            </div>
          </li>
        ))}
      </ul>

      {gateApplies && ownershipKnown && !isOwner ? (
        <div className='lum-approval-owner-note mt-2'>
          <p className='fw-semibold small mb-1'>
            {intl.formatMessage({ id: 'CHAT.APPROVAL.OWNER_ONLY' })}
          </p>
          <p className='small text-muted mb-0'>
            {intl.formatMessage({ id: 'CHAT.APPROVAL.OWNER_ONLY_WHY' })}
          </p>
        </div>
      ) : null}

      {/*
        Ownership still unknown. Deliberately NOT the owner-only denial above:
        that sentence is a claim about who this user is, and making it before
        the answer is in — or for ever, when the read failed — tells the actual
        owner something false about their own chat.
      */}
      {gateApplies && !ownershipKnown ? (
        <p className='lum-approval-owner-note small text-muted mt-2 mb-0'>
          {intl.formatMessage({ id: 'CHAT.APPROVAL.OWNER_UNKNOWN' })}
        </p>
      ) : null}

      {/*
        Mounted unconditionally. A live region that appears at the same moment
        as its own text is frequently not announced at all — the region has to
        already exist for the change to be a change. "Waiting on N more" is
        precisely the message a keyboard user needs to hear without looking, so
        the empty `<p>` is load-bearing rather than dead markup; with no
        content and no margin it contributes no line box and no space.
      */}
      <p
        className={`lum-approval-status small text-muted mb-0${status ? ' mt-2' : ''}`}
        role='status'
      >
        {status}
      </p>
    </div>
  )
}

export default ApprovalCard
